import { useEffect, useRef, useState } from 'react'
import {
  Mic2,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Loader2,
  Linkedin,
  Instagram,
  Briefcase,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useMentors,
  useCreateMentor,
  useUpdateMentor,
  useDeactivateMentor,
  type Mentor,
  type CreateMentorDTO,
} from '@/services/super-admin/mentors.service'
import { useUploadMentorPhoto } from '@/services/upload.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

/**
 * Tela `Mentores` no super_admin. CRUD simples — lista em cards +
 * dialog de create/edit + confirm de desativação.
 *
 * Mentor é o instrutor das masterclasses; quando admin cria/edita um
 * curso `type='masterclass'` o dropdown de mentores é populado por
 * essa lista.
 */
export function MentorsManagement() {
  const { data: mentors, isLoading } = useMentors({ activeOnly: false })
  const create = useCreateMentor()
  const update = useUpdateMentor()
  const deactivate = useDeactivateMentor()
  const uploadPhoto = useUploadMentorPhoto()
  const { toast } = useToast()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Mentor | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Mentor | null>(null)

  const [form, setForm] = useState<CreateMentorDTO>({ name: '' })
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)

  function openCreate() {
    setEditing(null)
    setForm({ name: '' })
    setPhotoFile(null)
    setPhotoPreview(null)
    setDialogOpen(true)
  }

  function openEdit(mentor: Mentor) {
    setEditing(mentor)
    setForm({
      name: mentor.name,
      role: mentor.role,
      company: mentor.company,
      bio: mentor.bio,
      linkedin: mentor.linkedin,
      instagram: mentor.instagram,
      photo: mentor.photo,
    })
    setPhotoFile(null)
    setPhotoPreview(mentor.photo)
    setDialogOpen(true)
  }

  function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  async function handleSubmit() {
    if (!form.name?.trim()) {
      toast({ title: 'Nome é obrigatório', variant: 'destructive' })
      return
    }

    let photoUrl: string | null | undefined = form.photo
    if (photoFile) {
      try {
        photoUrl = await uploadPhoto.mutateAsync(photoFile)
      } catch (err) {
        toast({
          title: 'Erro ao enviar foto',
          description: getErrorMessage(err),
          variant: 'destructive',
        })
        return
      }
    }

    const payload = { ...form, photo: photoUrl ?? null }
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...payload })
        toast({ title: 'Mentor atualizado' })
      } else {
        await create.mutateAsync(payload)
        toast({ title: 'Mentor criado' })
      }
      setDialogOpen(false)
    } catch (err) {
      toast({
        title: 'Erro ao salvar',
        description: getErrorMessage(err),
        variant: 'destructive',
      })
    }
  }

  async function handleDeactivate() {
    if (!confirmDelete) return
    try {
      await deactivate.mutateAsync(confirmDelete.id)
      toast({
        title: 'Mentor desativado',
        description: 'Cursos vinculados continuam apontando pra ele — atribua outro se necessário.',
      })
      setConfirmDelete(null)
    } catch (err) {
      toast({
        title: 'Erro ao desativar',
        description: getErrorMessage(err),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mic2 className="w-5 h-5 text-primary" />
              Mentores
            </CardTitle>
            <CardDescription>
              Instrutores das masterclasses. Cadastre aqui pra disponibilizar no form de criação de
              curso masterclass.
            </CardDescription>
          </div>
          <Button onClick={openCreate} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" />
            Novo Mentor
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : !mentors || mentors.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Nenhum mentor cadastrado ainda. Clique em "Novo Mentor" pra começar.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {mentors.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-lg border p-4 flex items-start gap-3 transition-colors ${
                    m.active
                      ? 'border-border/50 bg-muted/10 hover:bg-muted/20'
                      : 'border-border/30 bg-muted/5 opacity-60'
                  }`}
                >
                  {m.photo ? (
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="w-14 h-14 rounded-full object-cover border border-border/40"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center text-base font-semibold text-muted-foreground border border-border/40">
                      {m.name.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{m.name}</p>
                    {(m.role || m.company) && (
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <Briefcase className="w-3 h-3 shrink-0" />
                        {[m.role, m.company].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mt-1.5 text-muted-foreground">
                      {m.linkedin && <Linkedin className="w-3 h-3" />}
                      {m.instagram && <Instagram className="w-3 h-3" />}
                      {!m.active && (
                        <span className="text-[10px] uppercase tracking-wide ml-auto text-amber-500">
                          Inativo
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(m)}
                      className="h-7 w-7 p-0"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {m.active && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(m)}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Mentor' : 'Novo Mentor'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Atualize os dados do mentor.'
                : 'Cadastre o instrutor que ministrará a masterclass.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Foto */}
            <div className="space-y-2">
              <Label>Foto</Label>
              <div className="flex items-center gap-4">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover border border-border/40"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center text-muted-foreground border border-border/40">
                    <Mic2 className="w-7 h-7" />
                  </div>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => photoInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {photoPreview ? 'Trocar foto' : 'Enviar foto'}
                </Button>
              </div>
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="mentor-name">Nome *</Label>
              <Input
                id="mentor-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Fábio Câmara"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Cargo */}
              <div className="space-y-2">
                <Label htmlFor="mentor-role">Cargo</Label>
                <Input
                  id="mentor-role"
                  value={form.role ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  placeholder="Ex: CEO"
                />
              </div>
              {/* Empresa */}
              <div className="space-y-2">
                <Label htmlFor="mentor-company">Empresa</Label>
                <Input
                  id="mentor-company"
                  value={form.company ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  placeholder="Ex: FCamara"
                />
              </div>
            </div>

            {/* Mini CV — limitado a 500 caracteres pra caber no card do mentor
                na masterclass detail sem overflow. */}
            <div className="space-y-2">
              <Label htmlFor="mentor-bio">Mini CV ({(form.bio ?? '').length}/500)</Label>
              <Textarea
                id="mentor-bio"
                value={form.bio ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value.slice(0, 500) }))}
                placeholder="Breve descrição da trajetória, conquistas e áreas de atuação."
                rows={6}
                maxLength={500}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* LinkedIn */}
              <div className="space-y-2">
                <Label htmlFor="mentor-linkedin" className="flex items-center gap-1.5">
                  <Linkedin className="w-3.5 h-3.5" />
                  LinkedIn
                </Label>
                <Input
                  id="mentor-linkedin"
                  value={form.linkedin ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              {/* Instagram */}
              <div className="space-y-2">
                <Label htmlFor="mentor-instagram" className="flex items-center gap-1.5">
                  <Instagram className="w-3.5 h-3.5" />
                  Instagram
                </Label>
                <Input
                  id="mentor-instagram"
                  value={form.instagram ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))}
                  placeholder="@handle ou URL completa"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={create.isPending || update.isPending || uploadPhoto.isPending}
            >
              {create.isPending || update.isPending || uploadPhoto.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando…
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm desativação */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar mentor?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmDelete?.name}</strong> não vai mais aparecer no dropdown de
              masterclass. Cursos já vinculados a ele continuam apontando — você pode atribuir outro
              mentor a qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate}>Desativar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
